import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Server, ShieldAlert, AlertTriangle } from 'lucide-react';
import { useRunnerAttempts } from '../hooks/useOperator';
import { desktopDeepLinks } from '../lib/deep-links';
import { DeepLinkButton, StatusPill, Field } from '../components/operator-ui';

/**
 * WI-14 (OPERATOR-UI-PLACEMENT): read-only workers/attempts overview. Surfaces a
 * worker's draining status and the attempts it holds, with fencing state
 * (generation) per attempt, from GET /api/runners/:id/attempts. Read-only —
 * draining and fencing actions hand off to the native Aldan Desktop console.
 */
export default function WorkerAttempts() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useRunnerAttempts(id);

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="text-sm text-slate-400">Loading worker…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8">
        <Link
          to="/operator"
          className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Operator Console
        </Link>
        <div className="text-sm text-slate-500">Worker not found.</div>
      </div>
    );
  }

  const draining = data.status === 'draining';

  return (
    <div className="p-8">
      <div className="mb-6">
        <Link
          to="/operator"
          className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Operator Console
        </Link>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100">
              <Server className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-mono text-xl font-bold text-slate-900">{data.workerId}</h1>
                <StatusPill status={data.status} />
              </div>
              <p className="mt-0.5 text-sm text-slate-500">
                {data.assignedCount} / {data.maxInstances} assigned
              </p>
            </div>
          </div>
          <DeepLinkButton href={desktopDeepLinks.worker(data.workerId)} label="Open worker" />
        </div>
      </div>

      {draining && (
        <div className="mb-6 flex items-center gap-2 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-700">
          <AlertTriangle className="h-4 w-4" />
          Worker is draining. No new attempts are being assigned; in-flight attempts finish or
          hand off before it goes offline.
        </div>
      )}

      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-slate-900">Worker</h2>
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Field label="Status" value={data.status} />
          <Field label="Assigned" value={data.assignedCount} />
          <Field label="Max instances" value={data.maxInstances} />
          <Field label="Attempts held" value={data.attempts.length} />
        </dl>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold text-slate-900">Attempts</h2>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Attempt</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Run group</th>
                <th className="px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">Gen (fence)</th>
                <th className="px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.attempts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-slate-400">
                    No attempts on this worker
                  </td>
                </tr>
              )}
              {data.attempts.map((a) => {
                const needsReconcile = a.status === 'outcome-unknown';
                return (
                  <tr key={a.id} className="text-slate-700">
                    <td className="px-3 py-2 font-mono text-xs">{a.id}</td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          needsReconcile
                            ? 'inline-flex items-center gap-1 text-amber-600'
                            : ''
                        }
                      >
                        {needsReconcile && <ShieldAlert className="h-3.5 w-3.5" />}
                        {a.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {a.runGroupId ? (
                        <Link
                          to={`/operator/run-groups/${a.runGroupId}`}
                          className="font-mono text-xs text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-900"
                        >
                          {a.runGroupId}
                        </Link>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{a.attempt ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{a.generation ?? '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        <DeepLinkButton href={desktopDeepLinks.attempt(a.id)} label="Attempt" />
                        <DeepLinkButton href={desktopDeepLinks.evidence(a.id)} label="Evidence" />
                        {needsReconcile && (
                          <DeepLinkButton
                            href={desktopDeepLinks.reconciliation(a.id)}
                            label="Reconcile"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
